import { DocumentNode, gql } from '@apollo/client';
import {
  Box,
  Button,
  IconButton,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  Stack,
  Text,
  useDisclosure,
} from '@chakra-ui/react';
import { Dinero, isZero } from 'dinero.js';
import NextLink from 'next/link';
import { useRouter } from 'next/router';
import * as React from 'react';

import { CurrencyDisplay } from '@/components/Currency';
import { Card } from '@/imports/ui/chakra/components/Card';
import {
  Edit2Icon,
  MoreHorizontalIcon,
  PlusCircleIcon,
} from '@/imports/ui/chakra/feather';



import { AddPaymentMethodModal } from './components/AddPaymentMethodModal';
import { PaymentMethodEditor } from './components/PaymentMethodEditor';
import { DragIcon } from '@/imports/ui/chakra/customIcon/dragIcon';
import { PaymentInfoCard_PaymentMethodFragment } from './graphql-types/PaymentInfoCard_PaymentMethodFragment';

export const PaymentInfoCard: React.FC<{
  canCurrentUserAddPaymentMethod: boolean;
  paymentMethods: PaymentInfoCard_PaymentMethodFragment[];
  userId?: string;
  organizationId?: string;
  creditBalance: Dinero<number>;
  isEditing: boolean;
  hasBGColor?: boolean;
  newView?: boolean;
}> & {
  paymentMethodFragment: DocumentNode;
} = ({
  canCurrentUserAddPaymentMethod,
  paymentMethods,
  userId,
  organizationId,
  creditBalance,
  isEditing,
  hasBGColor = false,
  newView = false,
}) => {
  const hasPaymentMethods = paymentMethods.some(
    (paymentMethod) => paymentMethod.canCurrentUserRemoveThisPaymentMethod
  );

  const addPaymentMethodModal = useDisclosure();

  const router = useRouter();

  return (
    <Card  p={hasBGColor ? undefined : "6"}>
      <Stack
        direction="row"
        spacing="4"
        justifyContent="space-between"
        width="full"
        bg={hasBGColor ? "secondary.500" : undefined}
        p={hasBGColor ? "4" : undefined}
        borderTopLeftRadius="8px" borderTopRightRadius="8px"
      >
        <Text textStyle="xlBodyStrong" fontSize={hasBGColor ? "18px" : "lg"}>
          {hasBGColor && <DragIcon w="18px" h="20px" boxSize="20px" mr="2" color="primary.500"  position="relative" top="-3px" zIndex={99} />}
          Saved Payment Methods
        </Text>
        {(canCurrentUserAddPaymentMethod || hasPaymentMethods) && (
          <Box>
            <Menu>
              <MenuButton
                visibility={isEditing ? 'hidden' : undefined}
                size="sm"
                as={IconButton}
                variant={hasBGColor ? "ghost" : "outline"}
                bg={hasBGColor ? "transparent" : "transparent"}
                _hover={{ bg: hasBGColor ? "secondary.500" : "gray.100" }}
                _active={{ bg: hasBGColor ? "secondary.500" : "gray.100" }}
                _focus={{ boxShadow: 'none', bg: hasBGColor ? "transparent" : "transparent" }}
                color={hasBGColor ? "primary.500" : "gray.500"}
                aria-label="payment actions"
                icon={<MoreHorizontalIcon transform="rotate(90deg)" />}
                border={hasBGColor ? "0px solid" : '1px solid'}
                borderColor={hasBGColor ? "transparent" : "grey.200"}
              />
              <MenuList>
                {canCurrentUserAddPaymentMethod && (
                  <>
                    <MenuItem
                      icon={<PlusCircleIcon color={hasBGColor ? "primary.500" : "gray.600"} boxSize={hasBGColor ? "20px" : "16px"} />}
                      onClick={addPaymentMethodModal.onOpen}
                    >
                      Add a Payment Method
                    </MenuItem>
                    <AddPaymentMethodModal
                      userId={userId}
                      organizationId={organizationId}
                      isOpen={addPaymentMethodModal.isOpen}
                      onClose={addPaymentMethodModal.onClose}
                    />
                  </>
                )}

                {hasPaymentMethods && !isEditing && (
                  <NextLink href={`${router.asPath}/payment`}>
                    <MenuItem icon={<Edit2Icon  color={hasBGColor ? "primary.500" : "gray.600"} boxSize={hasBGColor ? "20px" : "16px"} />}>
                      Edit Payment Methods
                    </MenuItem>
                  </NextLink>
                )}
              </MenuList>
            </Menu>
          </Box>
        )}
      </Stack>
      

      <Stack spacing="4" p={hasBGColor ? "4" : '0'} mt={hasBGColor ? "0" : '4'}>
        {paymentMethods.map((paymentMethod) => {
          return (
            <PaymentMethodEditor
              key={paymentMethod._id}
              paymentMethod={paymentMethod}
              isEditing={isEditing}
              newView={newView}
            />
          );
        })}

        {paymentMethods.length === 0 && (
          <Text color="gray.500">No payment method has been added yet</Text>
        )}

        {!isZero(creditBalance) && (
          <Stack spacing="0">
            <Text textStyle="compactStrong">Credit</Text>
            <CurrencyDisplay
              value={creditBalance}
              color="green.400"
              textStyle="compactStrong"
              as="span"
            />
          </Stack>
        )}
      </Stack>
    </Card>
  );
};

PaymentInfoCard.paymentMethodFragment = gql`
  fragment PaymentInfoCard_PaymentMethodFragment on PaymentMethod {
    _id
    canCurrentUserRemoveThisPaymentMethod
    ...PaymentMethodEditor_PaymentMethodFragment
  }
  ${PaymentMethodEditor.paymentMethodFragment}
`;
